import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import SydButton from '../../commonDesign/Button'
import SydInput from '../../commonDesign/SydInput'
import SydSelect from '../../commonDesign/SydSelect'
import SydLabel from '../../commonDesign/SydLabel'
import SydModal from '../../commonDesign/SydModal'
import SydModalActions from '../../commonDesign/SydModal/SydModalActions'
import useBlueprintExport, { EXPORT_STATES } from './hooks/useBlueprintExport'
import { blueprintExportFormats } from './admin/blueprintExportFormats'

const ALL_FORMAT_OPTIONS = blueprintExportFormats.map(f => ({ label: f.label, value: f.key }))

const FILTER_SCOPE_OPTIONS = [
  { label: 'Export All Data', value: 'all' },
  { label: 'Export Filtered Data', value: 'filtered' }
]

function buildFormatOptions (formats) {
  if (!formats?.length) return ALL_FORMAT_OPTIONS
  return ALL_FORMAT_OPTIONS.filter(f => formats.includes(f.value))
}

function BlueprintExportModal ({
  open,
  onClose,
  blueprintCode,
  purposeCode,
  parameters,
  filters,
  subordinatePredicates,
  sortBy,
  sortDirection,
  exportConfig
}) {
  const formatOptions = buildFormatOptions(exportConfig?.formats)
  const hasActiveFilters = filters?.length > 0

  const [format, setFormat] = useState(formatOptions[0]?.value ?? 'csv')
  const [fileName, setFileName] = useState(
    exportConfig?.defaultFileName || `blueprint-${blueprintCode}`
  )
  const [filterScope, setFilterScope] = useState('filtered')

  const { startExport, exportState, isBusy, error, reset } = useBlueprintExport()

  useEffect(() => {
    if (open) {
      setFormat(formatOptions[0]?.value ?? 'csv')
      setFileName(exportConfig?.defaultFileName || `blueprint-${blueprintCode}`)
      setFilterScope(hasActiveFilters ? 'filtered' : 'all')
      reset()
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveFilters = useMemo(() => {
    if (filterScope === 'all') return []
    return filters || []
  }, [filterScope, filters])

  const handleExport = useCallback(() => {
    startExport({
      code: blueprintCode,
      purposeCode,
      parameters: parameters || {},
      filters: effectiveFilters,
      subordinatePredicates: subordinatePredicates || [],
      sortBy: sortBy || null,
      sortDirection: sortDirection || 'asc',
      format,
      fileName: fileName || null
    })
  }, [
    startExport, blueprintCode, purposeCode, parameters,
    effectiveFilters, subordinatePredicates, sortBy, sortDirection,
    format, fileName
  ])

  const handleClose = useCallback(() => {
    if (!isBusy) {
      reset()
      onClose()
    }
  }, [isBusy, reset, onClose])

  useEffect(() => {
    if (exportState === EXPORT_STATES.COMPLETE) {
      const timer = setTimeout(() => {
        reset()
        onClose()
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [exportState, reset, onClose])

  const statusMessage = (() => {
    switch (exportState) {
      case EXPORT_STATES.STARTING: return 'Starting export...'
      case EXPORT_STATES.POLLING: return 'Preparing export, please wait...'
      case EXPORT_STATES.DOWNLOADING: return 'Download starting...'
      case EXPORT_STATES.COMPLETE: return 'Export complete'
      default: return null
    }
  })()

  return (
    <SydModal
      title='Export Data'
      open={open}
      onClose={handleClose}
      size='small'
    >
      <div className='flex flex-col gap-md p-md'>
        <SydLabel label='Format'>
          <SydSelect
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            options={formatOptions}
            disabled={isBusy}
            minWidth='200px'
          />
        </SydLabel>

        {hasActiveFilters && (
          <SydLabel label='Data Scope'>
            <SydSelect
              value={filterScope}
              onChange={(e) => setFilterScope(e.target.value)}
              options={FILTER_SCOPE_OPTIONS}
              disabled={isBusy}
              minWidth='200px'
            />
          </SydLabel>
        )}

        <SydLabel label='File Name'>
          <div className='flex items-center gap-sm'>
            <div className='flex-1'>
              <SydInput
                value={fileName}
                onChange={(e) => setFileName(e?.target?.value ?? e)}
                placeholder='file name'
                disabled={isBusy}
              />
            </div>
            <span className='font-size-sm'>.{format}</span>
          </div>
        </SydLabel>

        {statusMessage && (
          <div className='font-size-sm' style={{ color: 'var(--text-color-subtle)' }} role='status' aria-live='polite'>
            {statusMessage}
          </div>
        )}

        {error && (
          <div className='font-size-sm' style={{ color: 'var(--color-danger-500)' }} role='alert' aria-live='assertive'>
            {error}
          </div>
        )}
      </div>

      <SydModalActions>
        <SydButton variant='secondary' onClick={handleClose} disabled={isBusy}>
          Cancel
        </SydButton>
        <SydButton
          variant='primary'
          onClick={handleExport}
          processing={isBusy}
          disabled={isBusy || !format}
        >
          Export
        </SydButton>
      </SydModalActions>
    </SydModal>
  )
}

BlueprintExportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  blueprintCode: PropTypes.string.isRequired,
  purposeCode: PropTypes.string.isRequired,
  parameters: PropTypes.object,
  filters: PropTypes.array,
  subordinatePredicates: PropTypes.array,
  sortBy: PropTypes.string,
  sortDirection: PropTypes.string,
  exportConfig: PropTypes.shape({
    enabled: PropTypes.bool,
    formats: PropTypes.arrayOf(PropTypes.string),
    defaultFileName: PropTypes.string
  })
}

export default BlueprintExportModal
